import { useState, useEffect } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import React from 'react';

import { compareMissingLast } from '@/utils/compareMissingLast';

type SortDirection = 'asc' | 'desc' | 'none';

function compareValues(aValue: unknown, bValue: unknown, direction: SortDirection): number {
  // A missing value (e.g. a draft's unknown amount, FIN-413) sorts last in both directions.
  const missing = compareMissingLast(aValue, bValue);
  if (missing !== null) return missing;

  const sign = direction === 'asc' ? 1 : -1;
  if (typeof aValue === 'string' && typeof bValue === 'string') {
    return sign * aValue.localeCompare(bValue);
  }
  if (typeof aValue === 'number' && typeof bValue === 'number') {
    return sign * (aValue - bValue);
  }
  return sign * String(aValue).localeCompare(String(bValue));
}

export function useSortableTable<T>(defaultSortKey: keyof T | null = null, defaultSortDirection: SortDirection = 'none') {
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | null;
    direction: SortDirection;
  }>({
    key: defaultSortKey,
    direction: defaultSortDirection,
  });

  // Reset to default sort when component remounts (e.g., when returning to the page)
  useEffect(() => {
    if (defaultSortKey) {
      setSortConfig({
        key: defaultSortKey,
        direction: defaultSortDirection,
      });
    }
  }, [defaultSortKey, defaultSortDirection]);

  const handleSort = (key: keyof T) => {
    setSortConfig((currentConfig) => {
      if (currentConfig.key === key) {
        // Cycle through: asc -> desc -> none
        if (currentConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        if (currentConfig.direction === 'desc') {
          return { key: null, direction: 'none' };
        }
      }
      // First click or different key
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key: keyof T) => {
    if (sortConfig.key !== key) {
      return null;
    }
    
    if (sortConfig.direction === 'asc') {
      return React.createElement(ArrowUp, { className: "h-4 w-4" });
    }
    
    if (sortConfig.direction === 'desc') {
      return React.createElement(ArrowDown, { className: "h-4 w-4" });
    }
    
    return null;
  };

  const sortedItems = <U extends T>(items: U[], customSort?: (a: U, b: U, key: keyof T, direction: SortDirection) => number): U[] => {
    if (!sortConfig.key || sortConfig.direction === 'none') {
      return items;
    }

    return [...items].sort((a, b) => {
      if (customSort) {
        return customSort(a, b, sortConfig.key!, sortConfig.direction);
      }

      const aValue = a[sortConfig.key!];
      const bValue = b[sortConfig.key!];

      if (aValue === bValue) {
        return 0;
      }

      // Handle date comparison for common date fields
      if (
        typeof aValue === 'string' && 
        typeof bValue === 'string' && 
        (
          String(sortConfig.key).toLowerCase().includes('date') ||
          String(sortConfig.key).toLowerCase().includes('time')
        )
      ) {
        const aDate = new Date(aValue).getTime();
        const bDate = new Date(bValue).getTime();
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      return compareValues(aValue, bValue, sortConfig.direction);
    });
  };

  return { 
    sortConfig, 
    handleSort, 
    renderSortIcon,
    sortedItems
  };
}
